import { Button, Callout, Modal, PropertyList, PropertyListItem, TextArea } from '@humanforce/mango-ui-react';
import type { BsbValidationFinding } from './bsbValidationApiTypes';
import { bankingFindingLabel } from './payRunBankingModel';
import { useMangoDialogHeadingBridge, useMangoFieldErrorBridge } from './PayRunActionDialogs';

const TEST_ID = 'pay-run-banking-override-modal';
const REASON_FIELD_ID = 'pay-run-banking-override-reason';

interface PayRunBankingOverrideModalProps {
  finding: BsbValidationFinding | null;
  reason: string;
  busy: boolean;
  error: string | null;
  reasonError: string | null;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const directoryStatusLabel = (status: string): string => {
  switch (status) {
    case 'active': return 'Active';
    case 'merged': return 'Merged';
    case 'withdrawn': return 'Withdrawn';
    case 'unknown': return 'Unknown';
    default: return status;
  }
};

/**
 * Per-finding override confirmation. Reuses the existing Pay Run Modal shape and its two feature-local Mango
 * accessibility bridges. A reason is mandatory and is validated before any request is sent.
 */
export const PayRunBankingOverrideModal = ({
  finding,
  reason,
  busy,
  error,
  reasonError,
  onReasonChange,
  onConfirm,
  onClose,
}: PayRunBankingOverrideModalProps) => {
  const visible = finding !== null;
  useMangoDialogHeadingBridge(visible, TEST_ID);
  useMangoFieldErrorBridge(reasonError, REASON_FIELD_ID, TEST_ID);

  if (!finding) return null;

  return (
    <Modal
      visible={visible}
      header="Override banking finding?"
      testId={TEST_ID}
      role="alertdialog"
      ariaDescribedby={`${TEST_ID}-description`}
      closable={!busy}
      dismissableMask={!busy}
      closeOnEscape={!busy}
      onHide={() => { if (!busy) onClose(); }}
      footer={(
        <>
          <Button label="Cancel" variant="secondary" disabled={busy} onClick={onClose} />
          <Button label="Override" loading={busy} disabled={busy} onClick={onConfirm} />
        </>
      )}
    >
      <div className="mgo-stack mgo-gap-l">
        <p id={`${TEST_ID}-description`} className="mgo-text-s mgo-color-muted">
          Overriding records an audit entry against this finding only. It does not change the worker&apos;s banking
          details and does not reroute the payment. It cannot be undone.
        </p>
        <PropertyList>
          <PropertyListItem label="Worker">{finding.workerDisplayName ?? 'Name unavailable'}</PropertyListItem>
          <PropertyListItem label="BSB">{finding.bsb}</PropertyListItem>
          <PropertyListItem label="Finding">{bankingFindingLabel(finding.ruleCode)}</PropertyListItem>
          <PropertyListItem label="Directory status">{directoryStatusLabel(finding.directoryStatus)}</PropertyListItem>
          {finding.supersededByBsb ? (
            <PropertyListItem label="Replacement BSB">{finding.supersededByBsb}</PropertyListItem>
          ) : null}
        </PropertyList>
        <TextArea
          id={REASON_FIELD_ID}
          label="Reason"
          required
          rows={4}
          characterLimit={1000}
          value={reason}
          status={reasonError ? 'critical' : ''}
          statusText={reasonError ?? undefined}
          onChange={(event) => onReasonChange(event.target.value)}
        />
        {error ? (
          <div role="alert">
            <Callout tone="critical" title="Override failed">{error}</Callout>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
